import { styled } from '@mui/material/styles';
import { Box, Typography, Button, Paper, TableCell, TableRow, Checkbox, FormControlLabel } from '@mui/material';
import CommonText from '../../../../form-components/CommonText';

// ============================================
// Container & Layout
// ============================================

export const PageContainer = styled(Box)({
  background: '#fff',
  borderRadius: '10px',
  width: '100%',
});

// ============================================
// Search Section
// ============================================

export const SearchSection = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
});

export const SearchInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const SearchLabel = styled(CommonText)({
  width: '110px',
  marginRight: '35px',
});

export const SearchButton = styled(Button)({
  background: '#707070',
  width: '62px',
  height: 40,
  marginLeft: '8px',
  '&:hover': {
    background: '#707070',
    color: '#fff',
  },
});

export const CreateButton = styled(Button)({
  height: '40px',
  borderRadius: '10px',
  background: '#15AFB7',
  color: '#fff',
  border: 'none',
  fontSize: '16px',
  fontWeight: 'bold',
  gap: '6px',
  '&:hover': {
    background: '#15AFB7',
    color: '#fff',
    border: 'none',
  },
});

// ============================================
// Filter Section
// ============================================

export const FilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginTop: '24px',
  minHeight: '24px',
});

export const FilterLabel = styled(CommonText)({
  width: '98px',
  minWidth: '98px',
  fontSize: '14px',
  marginRight: '8px',
});

export const FilterContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const AllCheckboxWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '70px',
  minWidth: '70px',
});

export const SalesOptionSection = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  marginTop: '24px',
});

export const SalesOptionLabel = styled(Typography)({
  width: '98px',
  minWidth: '98px',
  fontWeight: 500,
  fontSize: '14px',
  marginRight: '8px',
});

export const SalesOptionContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

export const SalesOptionRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  minHeight: '24px',
});

export const SubLabel = styled(Typography)({
  width: '70px',
  minWidth: '70px',
  fontSize: '14px',
  color: '#333',
});

export const DirectInputWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const DirectInputLabel = styled(Typography)({
  fontSize: '14px',
  marginRight: '4px',
});

export const DirectInputField = styled('input')({
  width: '48px',
  height: '24px',
  border: '1px solid #ddd',
  borderRadius: '4px',
  textAlign: 'center',
  fontSize: '14px',
  '&:focus': {
    outline: 'none',
    borderColor: '#0F7F85',
  },
});

export const DirectInputUnit = styled(Typography)({
  fontSize: '14px',
  marginLeft: '4px',
});

export const StyledCheckbox = styled(Checkbox)({
  padding: '0',
  marginRight: '4px',
});

export const CheckboxLabel = styled(FormControlLabel)({
  marginLeft: '0',
  width: '100px',
  marginRight: '11.5px',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
});

export const AllCheckboxLabel = styled(FormControlLabel)({
  marginRight: '0',
  marginLeft: '0',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
});

export const FilterDivider = styled('span')({
  display: 'inline-block',
  width: '2px',
  height: '20px',
  backgroundColor: '#ddd',
  marginLeft: '24px',
  marginRight: '24px',
});

export const FilterButtonWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: '16px',
  gap: '12px',
});

export const ApplyButton = styled(Button)({
  borderRadius: '8px',
  minWidth: 220,
  height: 40,
});

export const ClearButton = styled(Button)({
  minWidth: 140,
  height: 40,
  borderRadius: '8px',
  marginLeft: '8px',
});

// ============================================
// Divider
// ============================================

export const Divider = styled('hr')({
  border: '1px solid #e1e1e1',
  marginTop: '20px',
});

// ============================================
// Table Controls
// ============================================

export const TableControlsWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '16px',
});

export const PageInfo = styled(Typography)({});

export const SortWrapper = styled(Box)({});

// ============================================
// Table Styles
// ============================================

export const StyledTableCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  fontSize: '12px',
  fontWeight: 500,
});

export const StyledTableCellLast = styled(TableCell)({
  fontSize: '12px',
  fontWeight: 500,
});

export const StyledBodyCell = styled(TableCell)({
  borderRight: '1px solid #ddd',
  fontSize: '12px',
});

export const StyledBodyCellLast = styled(TableCell)({
  fontSize: '12px',
});

export const StyledTableRow = styled(TableRow, {
  shouldForwardProp: prop => prop !== 'ended',
})(({ ended }) => ({
  height: '40px',
  textDecoration: 'none',
  '&:last-child td, &:last-child th': {
    borderBottom: 0,
  },
  ...(ended && {
    backgroundColor: '#f9f9f9',
    '& td, & th': {
      color: '#bbb',
    },
  }),
  '&:hover': {
    backgroundColor: ended ? '#f3f3f3' : '#f9fafb',
  },
}));

// ============================================
// Status Colors
// ============================================

export const StatusText = styled('span', {
  shouldForwardProp: prop => prop !== 'status',
})(({ status }) => {
  const styles = {
    APPROVED: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
    ON_SALE: { color: '#0F7F85', backgroundColor: '#e6f5f5' },
    APPROVAL_PENDING: { color: '#5A5A5A', backgroundColor: '#f0f0f0' },
    REFUSED: { color: '#FFB659', backgroundColor: '#fff5e6' },
    EXPIRED: { color: '#EE735A', backgroundColor: '#fef0ed' },
    SALE_END: { color: '#EE735A', backgroundColor: '#fef0ed' },
  };
  const s = styles[status] || { color: '#262626', backgroundColor: '#f5f5f5' };

  return {
    display: 'inline-block',
    fontWeight: 600,
    fontSize: '11px',
    padding: '1px 8px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
    color: s.color,
    backgroundColor: s.backgroundColor,
  };
});

// ============================================
// Pagination
// ============================================

export const PaginationWrapper = styled(Box)({
  marginTop: '20px',
  display: 'flex',
  justifyContent: 'center',
});

export const paginationStyles = {
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium': {
    background: '#0F7F85',
    border: 'none',
    height: '24px',
    minWidth: '24px',
  },
  '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium.Mui-selected': {
    background: '#0F7F85',
    color: '#fff',
  },
  '.MuiPaginationItem-icon': {
    color: '#fff',
  },
};
